/**
 * ProjectComparison — shows one row per project with mini verdict bar,
 * primary KPI value with delta, and trend arrow.
 * Only rendered when projectId === 'all'.
 */

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { useVerdictSummary } from '@/hooks/useDashboard';
import { fmtPct, fmtKpiValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import type { ProjectComparisonRow } from '@/types/dashboard';

// ============================================================================
// CONSTANTS
// ============================================================================

const VERDICT_COLORS = {
  scale: '#22C55E',
  fix: '#F59E0B',
  kill: '#EF4444',
} as const;

// ============================================================================
// ROW COMPONENT
// ============================================================================

function ProjectRow({ row }: { row: ProjectComparisonRow }) {
  const total = row.verdictBar.scale + row.verdictBar.fix + row.verdictBar.kill;
  const scaleW = total > 0 ? Math.round((row.verdictBar.scale / total) * 100) : 0;
  const fixW = total > 0 ? Math.round((row.verdictBar.fix / total) * 100) : 0;
  const killW = total > 0 ? Math.round((row.verdictBar.kill / total) * 100) : 0;
  const trend = row.primaryKpi.trend;
  const hasTrend = trend != null;
  const trendPositive = hasTrend && trend > 0;

  return (
    <div className="flex items-center gap-3 py-2 border-b border-border-subtle last:border-0">
      <div className="min-w-0 w-32 flex-shrink-0">
        <div className="text-xs font-medium text-primary truncate">{row.projectName}</div>
      </div>

      {/* Mini verdict bar */}
      <div className="flex-1 flex h-3 rounded-full overflow-hidden">
        <div
          style={{ width: `${scaleW}%`, backgroundColor: VERDICT_COLORS.scale }}
          title={`Scale: ${fmtPct(scaleW, 0)}`}
        />
        <div
          style={{ width: `${fixW}%`, backgroundColor: VERDICT_COLORS.fix }}
          title={`Fix: ${fmtPct(fixW, 0)}`}
        />
        <div
          style={{ width: `${killW}%`, backgroundColor: VERDICT_COLORS.kill }}
          title={`Kill: ${fmtPct(killW, 0)}`}
        />
      </div>

      {/* Primary KPI */}
      <div className="flex-shrink-0 text-right w-28">
        <div className="text-xs font-semibold text-primary">
          {row.primaryKpi.label}: {fmtKpiValue(row.primaryKpi.value, row.primaryKpi.unit)}
        </div>
        {hasTrend && (
          <div
            className={cn(
              'text-[10px]',
              trendPositive ? 'text-success' : trend < 0 ? 'text-danger' : 'text-secondary'
            )}
          >
            {trend > 0 ? '+' : ''}{fmtPct(trend, 1)} vs prior
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ProjectComparison() {
  const { filters } = useDashboardContext();
  const { data } = useVerdictSummary(filters);

  // Only render when "All Projects" is selected
  if (filters.projectId !== 'all') return null;

  const rows = data?.projectComparison;
  if (!rows || rows.length === 0) return null;

  return (
    <div className="rounded-xl bg-surface shadow-sm border border-border-subtle p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-primary">By Project</div>
          <div className="text-xs text-secondary mt-0.5">
            Verdict split and primary KPI per project
          </div>
        </div>
        <div className="flex items-center gap-3">
          <CsvExport
            headers={['Project', 'Scale', 'Fix', 'Kill', 'Primary KPI', 'KPI Value', 'KPI Trend']}
            rows={rows.map((row) => [
              row.projectName,
              row.verdictBar.scale,
              row.verdictBar.fix,
              row.verdictBar.kill,
              row.primaryKpi.label,
              row.primaryKpi.value,
              row.primaryKpi.trend,
            ])}
            filename="project-comparison"
          />
          <span className="flex items-center gap-1 text-xs text-secondary">
            <span className="w-2 h-2 rounded-full bg-success" />
            Scale
          </span>
          <span className="flex items-center gap-1 text-xs text-secondary">
            <span className="w-2 h-2 rounded-full bg-warning" />
            Fix
          </span>
          <span className="flex items-center gap-1 text-xs text-secondary">
            <span className="w-2 h-2 rounded-full bg-danger" />
            Kill
          </span>
        </div>
      </div>

      <div className="space-y-0">
        {rows.map((row) => (
          <ProjectRow key={row.projectId} row={row} />
        ))}
      </div>
    </div>
  );
}
